import { Plus, Check, AlertCircle } from 'lucide-react';
import { plural } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react/macro';
import { cn } from '@/lib/utils';
import type { MenuItem } from '@/lib/api/services/orders.api';

interface Props {
  item: MenuItem;
  inCart: boolean;
  onAdd: (item: MenuItem) => void;
}

export function MenuItemCard({ item, inCart, onAdd }: Props) {
  const { t } = useLingui();

  return (
    <article
      className={cn(
        'bg-(--color-surface-content) border rounded-2xl overflow-hidden flex flex-col transition-colors',
        inCart && 'border-(--color-text-primary) ring-1 ring-foreground',
      )}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] bg-(--color-surface-muted) overflow-hidden">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.name}
            loading="lazy"
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-(--color-text-secondary) text-xs">
            <Trans>No image</Trans>
          </div>
        )}
        {item.allergens.length > 0 && (
          <span className="absolute left-[0.65rem] bottom-[0.65rem] inline-flex items-center gap-1 bg-(--color-surface-page)/95 px-2 py-1 rounded-full text-[0.6875rem] font-semibold text-(--color-text-primary)">
            <AlertCircle className="w-3 h-3" />
            {item.allergens.slice(0, 2).join(', ')}
            {item.allergens.length > 2 && ` +${item.allergens.length - 2}`}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-3 pb-4 flex flex-col gap-1.5 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-[1rem] font-bold text-(--color-text-primary) truncate">{item.name}</h3>
            {item.isFeatured && (
              <span className="shrink-0 text-[0.6875rem] font-bold px-1.5 py-0.5 rounded-full bg-(--color-action)/10 text-(--color-action)">
                <Trans>Featured</Trans>
              </span>
            )}
          </div>
          <button
            type="button"
            aria-label={inCart ? t`In order` : t`Add to order`}
            onClick={() => onAdd(item)}
            className={cn(
              'shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-colors',
              inCart
                ? 'bg-(--color-success) text-(--brand-white) hover:bg-(--color-success-hover)'
                : 'bg-(--color-text-primary) text-(--color-surface-page) hover:opacity-80',
            )}
          >
            {inCart ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          </button>
        </div>

        <p className="text-[0.78rem] text-(--color-text-secondary)">
          {item.station && <span>{item.station} · </span>}
          {t`${item.prepTimeMinutes}m prep`}
        </p>

        <div className="flex items-center justify-between mt-auto pt-1">
          <span className="text-[0.9rem] font-bold text-(--color-text-primary)">
            {item.price != null ? `$${item.price.toFixed(2)}` : '—'}
          </span>
          <span className="text-[0.6875rem] text-(--color-text-secondary)">
            {plural(item.recipes.length, { one: '# recipe', other: '# recipes' })}
          </span>
        </div>
      </div>
    </article>
  );
}
